import React, { useEffect, useLayoutEffect, useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';

let openModalCount = 0;

export const ModalHeader = ({ toggle, className, children }) => (
  <div className={classNames('modal-header', className)}>
    <h5 className="modal-title">{children}</h5>
    {toggle && (
      <button type="button" className="modal-close-btn" onClick={toggle} aria-label="Close">
        &times;
      </button>
    )}
  </div>
);

export const ModalBody = ({ className, children }) => (
  <div className={classNames('modal-body', className)}>{children}</div>
);

export const ModalFooter = ({ className, children }) => (
  <div className={classNames('modal-footer', className)}>{children}</div>
);

const Modal = ({
  children,
  isOpen,
  toggle,
  title,
  footer,
  noScroll,
  size,
  centered,
  fullscreen,
  className,
  modalClassName,
  contentClassName,
  scrollable,
  onClosed,
  ...otherProps
}) => {
  const [layerIndex, setLayerIndex] = useState(0);
  const mouseDownTargetRef = useRef(null);

  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape' && toggle) toggle();
    },
    [toggle],
  );

  // Mémorise l'élément où le clic a commencé pour ne fermer que sur un vrai clic-dehors.
  const handleOverlayMouseDown = (e) => {
    mouseDownTargetRef.current = e.target;
  };

  // Ne ferme que si le clic a commencé ET fini sur l'overlay lui-même.
  // Évite la fermeture accidentelle quand on sélectionne du texte dans la modale
  // et qu'on relâche le clic en dehors (mousedown dans le dialog, mouseup sur l'overlay).
  const handleOverlayClick = (e) => {
    if (toggle && e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget) {
      toggle();
    }
  };

  useLayoutEffect(() => {
    if (isOpen) {
      openModalCount += 1;
      setLayerIndex(openModalCount);
    }
    return () => {
      if (isOpen) {
        openModalCount -= 1;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (openModalCount === 0) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, handleEscape]);

  const Footer = footer && !React.isValidElement(footer) ? footer : null;

  const backdropZ = 1040 + (layerIndex - 1) * 10;
  const modalZ = 1045 + (layerIndex - 1) * 10;

  const modal = (
    <AnimatePresence onExitComplete={onClosed}>
      {isOpen && (
        <>
          <motion.div
            className="modal-backdrop"
            style={{ zIndex: backdropZ }}
            onMouseDown={handleOverlayMouseDown}
            onClick={handleOverlayClick}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
          <motion.div
            className={classNames('modal', modalClassName)}
            style={{ zIndex: modalZ }}
            onMouseDown={handleOverlayMouseDown}
            onClick={handleOverlayClick}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className={classNames('modal-dialog', className, {
                [`modal-${size}`]: size,
                'modal-fullscreen': fullscreen,
              })}
              onClick={(e) => e.stopPropagation()}
              initial={{ y: -30, scale: 0.95 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: -30, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <div className={classNames('modal-content', contentClassName)}>
                {title && <ModalHeader toggle={toggle}>{title}</ModalHeader>}
                {children && (
                  <>
                    {title ? <ModalBody>{children}</ModalBody> : children}
                  </>
                )}
                {React.isValidElement(footer) && <ModalFooter>{footer}</ModalFooter>}
                {Footer && (
                  <ModalFooter>
                    <Footer closeModal={toggle} />
                  </ModalFooter>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modal, document.body);
};

Modal.defaultProps = {
  title: null,
  toggle: null,
  footer: null,
  noScroll: false,
  size: null,
  centered: true,
  fullscreen: false,
  className: null,
  modalClassName: null,
  contentClassName: null,
  scrollable: true,
  onClosed: null,
};

Modal.propTypes = {
  children: PropTypes.any,
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  title: PropTypes.string,
  footer: PropTypes.any,
  noScroll: PropTypes.bool,
  size: PropTypes.string,
  centered: PropTypes.bool,
  fullscreen: PropTypes.bool,
  className: PropTypes.string,
  modalClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  scrollable: PropTypes.bool,
  onClosed: PropTypes.func,
};

export default Modal;
